// Rendering only. Takes interpolated entities + HUD data and paints
// them under a camera that follows the player. The world is infinite,
// so we generate just the platforms in view from the shared seed and
// draw the void below.

import { drawSprite, drawItemIcon, drawCustomBlockIcon } from "./sprites.js";
import { resolveWidgets } from "./hud-widgets.js";
import { TILE, tileX, tileY, VOID_Y, SPAWN } from "../../../engine/chunks.js";
import { fillOf, lineOf, isDoor, isDoorOpen } from "../../../engine/tiles.js";
import { paintColor } from "../../../engine/palette.js";
import { SUB, SUBTILE, present, hpAt, opacityOf } from "../../../engine/damage.js";
import { VERSION } from "../../../version.js";

const VIEW_H = 760; // world units visible top-to-bottom at rest; sets the zoom

// A "view" is the camera: where it's centred and how world maps to
// screen. `zoom` (1 = normal) multiplies how much world fits on
// screen, so 2 shows twice the height — everything that derives from
// `scale` (the world transform, the visible rect, screen<->world, and
// so aiming) follows from this one number.
export function computeView(canvas, cam, zoom = 1) {
    const scale = canvas.height / (VIEW_H * (zoom || 1));
    return { scale, camX: cam.x, camY: cam.y, cw: canvas.width, ch: canvas.height };
}

export function screenToWorld(view, sx, sy) {
    return {
        x: (sx - view.cw / 2) / view.scale + view.camX,
        y: (sy - view.ch / 2) / view.scale + view.camY,
    };
}

function visibleRect(view) {
    const hw = (view.cw / 2) / view.scale;
    const hh = (view.ch / 2) / view.scale;
    return { x0: view.camX - hw, y0: view.camY - hh, x1: view.camX + hw, y1: view.camY + hh };
}

function backdrop(g, view, rect) {
    // sky
    const sky = g.createLinearGradient(0, 0, 0, view.ch);
    sky.addColorStop(0, "#0b1020");
    sky.addColorStop(1, "#141d33");
    g.fillStyle = sky;
    g.fillRect(0, 0, view.cw, view.ch);
    // parallax stars in screen space (drift slower than the camera)
    g.fillStyle = "rgba(255,255,255,.06)";
    const px = -view.camX * 0.3, py = -view.camY * 0.3;
    const step = 70;
    const ox = ((px % step) + step) % step;
    const oy = ((py % step) + step) % step;
    for (let x = ox; x < view.cw; x += step) {
        for (let y = oy; y < view.ch; y += step) {
            g.fillRect(x + ((y * 3) % 20), y + ((x * 5) % 20), 2, 2);
        }
    }
}

// Scale a #rrggbb colour by a factor (>1 lightens, <1 darkens), clamped. Used to
// derive a door's overlay tones from whatever material it's cut into.
function shade(hex, f) {
    const n = parseInt(hex.slice(1), 16);
    const r = Math.min(255, ((n >> 16) & 255) * f) | 0;
    const gr = Math.min(255, ((n >> 8) & 255) * f) | 0;
    const b = Math.min(255, (n & 255) * f) | 0;
    return "#" + ((1 << 24) | (r << 16) | (gr << 8) | b).toString(16).slice(1);
}

// A door, drawn in the colours of the wall it's cut into (no fixed door tint). Closed
// = a solid two-leaf slab flush in the wall; open = a recessed passage with the leaves
// folded back against the posts, so you read open-vs-shut at a glance.
function drawDoor(g, tile, x, y) {
    const fill = fillOf(tile), line = lineOf(tile), leaf = shade(fill, 1.22);
    if (isDoorOpen(tile)) {
        g.fillStyle = shade(fill, 0.5); g.fillRect(x, y, TILE + 0.5, TILE + 0.5); // recessed opening
        g.fillStyle = leaf;
        g.fillRect(x + 3, y + 3, 5, TILE - 6);          // leaf folded to the left post
        g.fillRect(x + TILE - 8, y + 3, 5, TILE - 6);   // ...and the right
        g.strokeStyle = line; g.lineWidth = 2;
        g.strokeRect(x + 2, y + 2, TILE - 4, TILE - 4); // the frame
    } else {
        g.fillStyle = fill; g.fillRect(x, y, TILE + 0.5, TILE + 0.5); // flush in the wall
        g.fillStyle = leaf; g.fillRect(x + 4, y + 3, TILE - 8, TILE - 4); // the slab
        g.strokeStyle = line; g.lineWidth = 2;
        g.strokeRect(x + 4, y + 3, TILE - 8, TILE - 4);
        g.beginPath(); g.moveTo(x + TILE / 2, y + 3); g.lineTo(x + TILE / 2, y + TILE - 1); g.stroke(); // two leaves
        g.fillStyle = line;
        g.beginPath(); g.arc(x + TILE / 2 - 5, y + TILE / 2 + 3, 2, 0, Math.PI * 2); g.fill(); // handles
        g.beginPath(); g.arc(x + TILE / 2 + 5, y + TILE / 2 + 3, 2, 0, Math.PI * 2); g.fill();
    }
}

// Draw the tile terrain. Fill every visible wall tile in its material's colour, then
// stroke an edge ONLY where the neighbour is a DIFFERENT tile — empty air, a door, or
// a different material. Two adjacent tiles of the same kind share that internal edge
// and neither draws it, so a connected blob of one material reads as a single outlined
// platform; where wood meets rock (or a doorway breaks a wall) the seam is drawn.
// Doors are painted last, in a bespoke pass, in the colours of their wall.
// Is the sub-cell at (col,row) — possibly one step into a NEIGHBOUR tile — solid in the
// same material? Looking across the tile boundary is what lets a chipped tile blend
// seamlessly into the solid tiles around it: only a real hole (air) or a material change
// counts as an edge, so a broken block doesn't get boxed off from the identical block
// beside it. A whole neighbour tile is all-present; a chipped one is checked cell by cell.
function subCellSolid(terrain, tcx, tri, col, row, mat) {
    if (col < 0) { tcx--; col += SUB; } else if (col >= SUB) { tcx++; col -= SUB; }
    if (row < 0) { tri--; row += SUB; } else if (row >= SUB) { tri++; row -= SUB; }
    if (terrain.tileAt(tcx, tri) !== mat) return false;
    const nc = terrain.damageCells(tcx, tri);
    return nc === undefined ? true : present(nc, col, row);
}

// Outline a chipped tile: draw each surviving sub-cell's edge only where it faces air or a
// different material (in this tile OR the neighbour across the boundary). Same-material
// solid neighbours merge, so only the carved holes and true material seams get a line.
function strokeChipped(g, terrain, tcx, tri, cells) {
    const mat = terrain.tileAt(tcx, tri);
    const x0 = tcx * TILE, y0 = tri * TILE;
    const solid = (c, r) => subCellSolid(terrain, tcx, tri, c, r, mat);
    g.beginPath();
    for (let row = 0; row < SUB; row++) {
        for (let col = 0; col < SUB; col++) {
            if (!present(cells, col, row)) continue;
            const x = x0 + col * SUBTILE, y = y0 + row * SUBTILE;
            if (!solid(col, row - 1)) { g.moveTo(x, y); g.lineTo(x + SUBTILE, y); }
            if (!solid(col, row + 1)) { g.moveTo(x, y + SUBTILE); g.lineTo(x + SUBTILE, y + SUBTILE); }
            if (!solid(col - 1, row)) { g.moveTo(x, y); g.lineTo(x, y + SUBTILE); }
            if (!solid(col + 1, row)) { g.moveTo(x + SUBTILE, y); g.lineTo(x + SUBTILE, y + SUBTILE); }
        }
    }
    g.stroke();
}

// A dashed box at the tile a build tool would act on — the grid alignment isn't obvious
// from the crosshair alone. Green to place, amber to break; red when the action would
// no-op (a taken tile for the trowel, empty air for the mallet). Drawn in world space.
function drawBuildGhost(g, ghost) {
    const x = ghost.tcx * TILE, y = ghost.tri * TILE;
    g.save();
    g.setLineDash([5, 4]);
    g.lineWidth = 2;
    g.strokeStyle = !ghost.ok ? "rgba(255,91,110,.75)" : ghost.place ? "rgba(120,255,180,.9)" : "rgba(255,180,80,.95)";
    g.strokeRect(x + 1.5, y + 1.5, TILE - 3, TILE - 3);
    g.setLineDash([]);
    g.restore();
}

// A custom block (Set 19, the block maker): a tile whose 6×6 sub-cells are painted per its design — a
// palette colour where a pixel was coloured, and NOTHING where it wasn't (a hollow subtile, Set 20.2: a
// see-through hole that's also passable — placeCustom seeds the damage grid so the sub-cell is absent).
// The grid is the same 6×6 the damage system uses, so a custom block reads at the resolution a chipped
// block does. `def` is 36 palette indices row-major (-1 = hollow).
function drawCustomBlock(g, tcx, tri, def) {
    const x0 = tcx * TILE, y0 = tri * TILE;
    for (let row = 0; row < SUB; row++) for (let col = 0; col < SUB; col++) {
        const idx = def ? def[row * SUB + col] : -1;
        if (idx < 0) continue; // hollow subtile (Set 20.2) — a see-through hole, drawn as nothing
        g.fillStyle = paintColor(idx);
        g.fillRect(x0 + col * SUBTILE, y0 + row * SUBTILE, SUBTILE + 0.5, SUBTILE + 0.5);
    }
}

function drawTiles(g, terrain, rect) {
    const at = (tcx, tri) => terrain.tileAt(tcx, tri); // live terrain: base + edits
    const c0 = tileX(rect.x0) - 1, c1 = tileX(rect.x1) + 1;
    const r0 = tileY(rect.y0) - 1, r1 = tileY(rect.y1) + 1;

    // Pass 1: fills (walls only). Doing all fills before any strokes keeps a stroke
    // from being painted over by a neighbour's fill. A CHIPPED tile (it carries a 6×6 HP
    // grid) fills its surviving sub-cells at an opacity that tracks their health, so a
    // block visibly weakens — fading, then popping — as it's worn down. A PAINTED tile
    // (Set 19.1) carries a 6×6 colour grid; painted sub-cells draw in the palette colour,
    // the rest in the material fill — so a splat is a circle of coloured pixels. Paint and
    // damage compose per sub-cell (a painted, chipped tile shows its surviving painted pixels).
    for (let tri = r0; tri <= r1; tri++) {
        for (let tcx = c0; tcx <= c1; tcx++) {
            const tile = at(tcx, tri);
            if (tile === 0 || isDoor(tile)) continue;
            const cid = terrain.customAt(tcx, tri);        // a custom block? (Set 19) — draw its 6×6 pixel design
            if (cid !== undefined) { drawCustomBlock(g, tcx, tri, terrain.blockDef(cid)); continue; }
            const base = fillOf(tile);
            const cells = terrain.damageCells(tcx, tri);   // 6×6 HP grid, or undefined for a whole tile
            const pg = terrain.paintAt(tcx, tri);          // 6×6 paint grid (Set 19.1), or undefined
            if (cells === undefined && pg === undefined) { // whole, unpainted — the fast path
                g.fillStyle = base;
                g.fillRect(tcx * TILE, tri * TILE, TILE + 0.5, TILE + 0.5);
                continue;
            }
            const x0 = tcx * TILE, y0 = tri * TILE;
            for (let row = 0; row < SUB; row++) for (let col = 0; col < SUB; col++) {
                if (cells !== undefined) {                 // respect chipping: a carved sub-cell is gone
                    const hp = hpAt(cells, col, row);
                    if (hp <= 0) continue;
                    g.globalAlpha = opacityOf(hp);
                }
                const pv = pg ? pg[row * SUB + col] : 0;   // 0 = unpainted → material fill
                g.fillStyle = pv ? paintColor(pv - 1) : base;
                g.fillRect(x0 + col * SUBTILE, y0 + row * SUBTILE, SUBTILE + 0.5, SUBTILE + 0.5);
            }
            g.globalAlpha = 1;
        }
    }

    // Pass 2: the shared outline (walls only) — one line segment per face exposed to a
    // DIFFERENT material, so same-material runs merge into one mass (incl. chipped tiles,
    // which blend with their solid neighbours). A chipped tile instead outlines its
    // surviving sub-cells' exposed edges (the carved holes), seamlessly with the terrain.
    g.lineWidth = 1.5;
    for (let tri = r0; tri <= r1; tri++) {
        for (let tcx = c0; tcx <= c1; tcx++) {
            const tile = at(tcx, tri);
            if (tile === 0 || isDoor(tile)) continue;
            const x = tcx * TILE, y = tri * TILE;
            g.strokeStyle = lineOf(tile); // the material's outline — paint is a per-sub-cell fill, not a whole-tile tint
            const cells = terrain.damageCells(tcx, tri);
            if (cells !== undefined) { strokeChipped(g, terrain, tcx, tri, cells); continue; }
            const edge = (nc, nr) => at(nc, nr) !== tile; // merge same-material neighbours
            g.beginPath();
            if (edge(tcx, tri - 1)) { g.moveTo(x, y); g.lineTo(x + TILE, y); }             // top
            if (edge(tcx, tri + 1)) { g.moveTo(x, y + TILE); g.lineTo(x + TILE, y + TILE); } // bottom
            if (edge(tcx - 1, tri)) { g.moveTo(x, y); g.lineTo(x, y + TILE); }             // left
            if (edge(tcx + 1, tri)) { g.moveTo(x + TILE, y); g.lineTo(x + TILE, y + TILE); } // right
            g.stroke();
        }
    }

    // Pass 3: doors, each in the colours of its wall.
    for (let tri = r0; tri <= r1; tri++) {
        for (let tcx = c0; tcx <= c1; tcx++) {
            const tile = at(tcx, tri);
            if (isDoor(tile)) drawDoor(g, tile, tcx * TILE, tri * TILE);
        }
    }
}

// The void, drawn in world space beneath VOID_Y: a warning band then
// a fade to black-red so falling reads as obvious danger.
function drawVoid(g, rect) {
    if (rect.y1 <= VOID_Y) return;
    const x0 = rect.x0 - 40, w = rect.x1 - rect.x0 + 80;
    const grd = g.createLinearGradient(0, VOID_Y, 0, VOID_Y + 700);
    grd.addColorStop(0, "rgba(120,20,30,.0)");
    grd.addColorStop(0.12, "rgba(120,20,30,.5)");
    grd.addColorStop(1, "rgba(8,4,8,.98)");
    g.fillStyle = grd;
    g.fillRect(x0, VOID_Y, w, rect.y1 - VOID_Y + 40);
    // danger line at the lip of the void
    g.strokeStyle = "rgba(255,80,90,.7)";
    g.lineWidth = 3;
    g.beginPath();
    g.moveTo(x0, VOID_Y);
    g.lineTo(x0 + w, VOID_Y);
    g.stroke();
}

export function drawScene(g, canvas, view, ents, particles, hud) {
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.fillStyle = "#080b12";
    g.fillRect(0, 0, canvas.width, canvas.height);

    const rect = visibleRect(view);
    backdrop(g, view, rect);

    // world-space transform: centre the camera on screen
    g.setTransform(view.scale, 0, 0, view.scale, view.cw / 2 - view.camX * view.scale, view.ch / 2 - view.camY * view.scale);
    drawVoid(g, rect);
    drawTiles(g, hud.terrain, rect);
    if (hud.buildGhost) drawBuildGhost(g, hud.buildGhost);

    const zonesFirst = ents.slice().sort((a, b) => (a.sh === "zone" ? -1 : b.sh === "zone" ? 1 : a.y - b.y));
    for (const e of zonesFirst) drawSprite(g, e);

    for (const p of particles) {
        const t = p.ttl / p.max;
        g.globalAlpha = t;
        if (p.fill) {                                  // a filled droplet (water splash) — shrinks as it fades
            g.fillStyle = p.color;
            g.beginPath();
            g.arc(p.x, p.y, Math.max(0.5, p.r0 * t), 0, Math.PI * 2);
            g.fill();
        } else {                                       // an expanding ring (hit / boom / splash burst)
            g.strokeStyle = p.color;
            g.lineWidth = 2;
            g.beginPath();
            g.arc(p.x, p.y, p.r0 + (1 - t) * p.grow, 0, Math.PI * 2);
            g.stroke();
        }
        g.globalAlpha = 1;
    }

    g.setTransform(1, 0, 0, 1, 0, 0);
    // Night: an ambient darkness from env.light (day/night plugin). 1 = full day (no tint); the
    // darker it gets the heavier the bluish overlay — laid over the world but UNDER the name tags,
    // markers and HUD below, so those stay readable at midnight.
    if (hud.light != null && hud.light < 1) {
        g.fillStyle = `rgba(6, 10, 32, ${((1 - hud.light) * 0.82).toFixed(3)})`;
        g.fillRect(0, 0, canvas.width, canvas.height);
    }
    drawNameTags(g, view, ents, hud);
    drawOffscreenPlayers(g, view, ents, hud);
    drawEnemyMarkers(g, view, ents, hud);
    drawBeaconArrows(g, view, ents);
    drawHud(g, canvas, hud);

    // Reactive HUD widgets (UI framework A): whatever plugins DECLARED via plugin.hud (hud.widgets,
    // from welcome), drawn from the local player's pushed stats (hud.self.hud). The hunger food bar is
    // now just one of these — no core code names it. Stacked bottom-left.
    drawWidgets(g, canvas, resolveWidgets(hud.widgets, hud.self && hud.self.hud));
}

// Paint the resolved reactive-HUD widgets (hud-widgets.js) in a bottom-left stack.
function drawWidgets(g, canvas, resolved) {
    if (!resolved.length) return;
    const bw = 118, bh = 7, bx = 18;
    let by = canvas.height - 30;
    g.textAlign = "left"; g.textBaseline = "alphabetic";
    for (const w of resolved) {
        g.font = "10px system-ui, sans-serif";
        if (w.kind === "bar") {
            g.fillStyle = "#9fb0d6"; g.fillText(w.label, bx, by - 5);
            g.fillStyle = "rgba(0,0,0,0.45)"; g.fillRect(bx - 2, by - 2, bw + 4, bh + 4);
            g.fillStyle = w.color; g.fillRect(bx, by, bw * w.frac, bh);
            by -= 24;
        } else {
            g.fillStyle = "#9fb0d6"; g.fillText(w.label, bx, by - 5);
            g.fillStyle = w.color; g.font = "13px system-ui, sans-serif"; g.fillText(String(w.value), bx, by + 6);
            by -= 26;
        }
    }
}

// (KotH's ring + crown and CP's point rings moved to their plugins' client.lua in Set 17 —
// the markers are ordinary display-bag entities now; see drawBeaconArrows for the off-screen story.)

// (CTF's base rings moved to the ctf plugin's client.lua in Set 17 — the base is an ordinary
// beacon-tagged entity now, and drawBeaconArrows below points at it when it's off-screen.)


// Survival radar: red threat markers on the screen edge for enemies within RANGE of
// you that are off-screen, so you can see where the horde is closing from. When none
// are in range, a lone gold arrow points back to the origin spawn — the enemies come
// from there, so it tells you which way to head to find the fight again.
const ENEMY_RANGE = 1600; // world units you can "sense" enemies within
function drawEnemyMarkers(g, view, ents, hud) {
    if (!hud.mode || hud.mode.name !== "coop") return; // survival only
    const self = hud.self ? ents.find((e) => e.id === hud.self.id) : null;
    if (!self) return;
    const cx = view.cw / 2, cy = view.ch / 2;
    const inL = 40, inR = 40, inT = 40, inB = 100; // clear of the HUD along the bottom
    const clampEdge = (dx, dy) => {
        const ts = [];
        if (dx > 0) ts.push((view.cw - inR - cx) / dx); else if (dx < 0) ts.push((inL - cx) / dx);
        if (dy > 0) ts.push((view.ch - inB - cy) / dy); else if (dy < 0) ts.push((inT - cy) / dy);
        const t = ts.length ? Math.min(...ts) : 0;
        return [cx + dx * t, cy + dy * t];
    };
    const onScreen = (sx, sy) => sx >= inL && sx <= view.cw - inR && sy >= inT && sy <= view.ch - inB;

    let inRange = 0;
    for (const e of ents) {
        if (e.tm !== "enemies") continue;
        const wdx = e.x - self.x, wdy = e.y - self.y;
        if (wdx * wdx + wdy * wdy > ENEMY_RANGE * ENEMY_RANGE) continue; // beyond your senses
        inRange++;
        const sx = (e.x - view.camX) * view.scale + cx;
        const sy = (e.y - view.camY) * view.scale + cy;
        if (onScreen(sx, sy)) continue; // you can already see it
        const [ex, ey] = clampEdge(sx - cx, sy - cy);
        arrowMarker(g, ex, ey, Math.atan2(sy - cy, sx - cx), "#ff3b3b", 12, 8);
    }
    if (inRange === 0) {
        // Nobody nearby — point home to the origin, where the enemies spawn.
        const sx = (SPAWN.x - view.camX) * view.scale + cx;
        const sy = (SPAWN.y - view.camY) * view.scale + cy;
        if (onScreen(sx, sy)) return; // already looking at spawn
        const [ex, ey] = clampEdge(sx - cx, sy - cy);
        arrowMarker(g, ex, ey, Math.atan2(sy - cy, sx - cx), "#ffe24a", 16, 9);
    }
}

// A gold-or-team arrow at the screen edge pointing to a WORLD point whenever it's off-screen
// (nothing if it's already in view) — the same marker survival uses to point you home to
// spawn. Shared by the KotH crown and the CTF bases so an objective is never a mystery.
function pointArrow(g, view, wx, wy, color, len = 16, wid = 9) {
    const cx = view.cw / 2, cy = view.ch / 2;
    const inL = 40, inR = 40, inT = 40, inB = 100; // clear of the HUD along the bottom
    const sx = (wx - view.camX) * view.scale + cx;
    const sy = (wy - view.camY) * view.scale + cy;
    if (sx >= inL && sx <= view.cw - inR && sy >= inT && sy <= view.ch - inB) return;
    const dx = sx - cx, dy = sy - cy;
    const ts = [];
    if (dx > 0) ts.push((view.cw - inR - cx) / dx); else if (dx < 0) ts.push((inL - cx) / dx);
    if (dy > 0) ts.push((view.ch - inB - cy) / dy); else if (dy < 0) ts.push((inT - cy) / dy);
    const t = ts.length ? Math.min(...ts) : 0;
    arrowMarker(g, cx + dx * t, cy + dy * t, Math.atan2(dy, dx), color, len, wid);
}


// BEACONS (Set 17): an arrow to any beacon-tagged entity when it's off-screen, in the entity's own
// colour. A ctf base wears one so a flag carrier always knows which way to run it home; any plugin
// can tag an objective the same way — the component is the channel, not a per-mode snapshot field.
function drawBeaconArrows(g, view, ents) {
    for (const e of ents) if (e.bn) pointArrow(g, view, e.x, e.y, e.c || "#cdd8f5", 14, 8);
}


// A filled arrowhead pointing outward at (x, y), rotated to `ang`, in `color`.
function arrowMarker(g, x, y, ang, color, len, wid) {
    g.save();
    g.translate(x, y);
    g.rotate(ang);
    g.fillStyle = color;
    g.beginPath();
    g.moveTo(len, 0); g.lineTo(-wid * 0.6, -wid); g.lineTo(-wid * 0.6, wid); g.closePath();
    g.fill();
    g.lineWidth = 1.5;
    g.strokeStyle = "rgba(0,0,0,.6)";
    g.stroke();
    g.restore();
}

// A floating name above each player's head, in their own colour, so you can tell who's
// who in a scrum. Only entities that appear in the scoreboard (i.e. players) get one.
function drawNameTags(g, view, ents, hud) {
    if (!hud.scores || !hud.scores.length) return;
    const names = new Map(hud.scores.map((s) => [s.id, s.name]));
    const cx = view.cw / 2, cy = view.ch / 2;
    g.textAlign = "center";
    g.textBaseline = "bottom";
    g.font = "700 12px system-ui, sans-serif";
    g.lineWidth = 3;
    for (const e of ents) {
        const name = names.get(e.id);
        if (!name) continue;
        const sx = (e.x - view.camX) * view.scale + cx;
        const sy = (e.y - view.camY) * view.scale + cy;
        if (sx < -60 || sx > view.cw + 60 || sy < 0 || sy > view.ch) continue; // off-screen
        const ty = sy - (e.r || 18) * view.scale * 1.9 - 6; // just above the head
        g.strokeStyle = "rgba(0,0,0,.7)";
        g.strokeText(name, sx, ty);
        g.fillStyle = e.c || "#cdd8f5";
        g.fillText(name, sx, ty);
    }
}

// Off-screen player markers: the world is infinite and other players wander far, so a
// coloured dot with an arrow rides the screen edge pointing at each player who's out
// of view — teammates and rivals alike, tinted the colour they're drawn in. Screen
// space, so it runs after the world is painted. Only the players in the scoreboard
// count (never enemies), and never yourself.
function drawOffscreenPlayers(g, view, ents, hud) {
    if (!hud.scores || hud.scores.length < 2) return; // nobody else to point at
    const playerIds = new Set(hud.scores.map((s) => s.id));
    const selfId = hud.self ? hud.self.id : null;
    const cx = view.cw / 2, cy = view.ch / 2;
    // Insets keep the whole marker on-screen and out from under the HUD — the weapon
    // row and health bar sit along the bottom, so the bottom edge is pulled up above
    // them rather than letting a pointer hide behind the weapons.
    const inL = 26, inR = 26, inT = 26, inB = 90;
    for (const e of ents) {
        if (!playerIds.has(e.id) || e.id === selfId) continue;
        const sx = (e.x - view.camX) * view.scale + cx;
        const sy = (e.y - view.camY) * view.scale + cy;
        if (sx >= inL && sx <= view.cw - inR && sy >= inT && sy <= view.ch - inB) continue; // on-screen (clear of HUD)
        // Clamp to the inset rectangle along the line from centre to the player.
        const dx = sx - cx, dy = sy - cy;
        const ts = [];
        if (dx > 0) ts.push((view.cw - inR - cx) / dx); else if (dx < 0) ts.push((inL - cx) / dx);
        if (dy > 0) ts.push((view.ch - inB - cy) / dy); else if (dy < 0) ts.push((inT - cy) / dy);
        const t = ts.length ? Math.min(...ts) : 0;
        const ex = cx + dx * t, ey = cy + dy * t;
        const color = e.c || "#39d1ff";
        // arrowhead pointing outward (toward the player, past the edge)
        g.save();
        g.translate(ex, ey);
        g.rotate(Math.atan2(dy, dx));
        g.fillStyle = color;
        g.beginPath();
        g.moveTo(13, 0); g.lineTo(2, -6); g.lineTo(2, 6); g.closePath();
        g.fill();
        g.restore();
        // the dot itself
        g.beginPath();
        g.arc(ex, ey, 6, 0, Math.PI * 2);
        g.fillStyle = color;
        g.fill();
        g.lineWidth = 1.5;
        g.strokeStyle = "rgba(0,0,0,.55)";
        g.stroke();
    }
}

const TEAM_INK = { red: "#ff5b6e", blue: "#4aa8ff" };
const clock = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// The scoreboard. Top-right, because that's where an FPS puts it and
// because the top-left is already the wave/chat column.
//
// What it shows follows the mode, not a flag the caller passes: a
// co-op room scores points, a deathmatch scores frags, and a team
// deathmatch scores both the sides and the people on them. Tab
// expands it to everyone with deaths.
function drawScores(g, canvas, hud) {
    const mode = hud.mode;
    if (!mode) return;
    const frags = mode.scoring === "frags";
    const hill = mode.scoring === "hilltime";      // King of the Hill: rows are seconds held
    const ctrl = mode.scoring === "control";       // Control Points: rows are points banked
    const stat = (s) => hill ? `${Math.floor(s.score)}s` : frags ? (hud.showScores ? `${s.frags} / ${s.deaths}` : `${s.frags}`) : `${Math.floor(s.score)}`;
    const x = canvas.width - 14;
    let y = 12;

    g.textAlign = "right";
    g.textBaseline = "top";

    // Room + limits: what game you're in and what ends it.
    if (hud.room) {
        g.font = "700 12px system-ui, sans-serif";
        g.fillStyle = "#ffe24a";
        g.fillText(hud.room.name.toUpperCase(), x, y);
        y += 15;
        g.font = "600 11px system-ui, sans-serif";
        g.fillStyle = "#6f7fa6";
        const lim = [];
        if (mode.fragLimit) lim.push(hill ? `${mode.fragLimit}s crown` : ctrl ? `${mode.fragLimit} pts` : `${mode.fragLimit} frags`);
        if (mode.timeLimit && hud.match) lim.push(clock(hud.match.remaining));
        g.fillText(lim.length ? `${mode.label} · ${lim.join(" · ")}` : mode.label, x, y);
        y += 18;
        // King of the Hill: whose crown it is right now (or that it's contested / open). Fed by the
        // koth plugin through env (hillKing / hillContested) — the same channel the wave counter
        // rides; core never learns what a hill is, only that something published a holder.
        if (hill) {
            g.font = "700 11px system-ui, sans-serif";
            g.fillStyle = hud.hillKing ? "#ffe24a" : hud.hillContested ? "#ff8a5b" : "#6f7fa6";
            g.fillText(hud.hillKing ? `👑 ${hud.hillKing}` : hud.hillContested ? "hill contested" : "hill open", x, y);
            y += 17;
        }
    }

    // Team totals first — in TDM the side's score is the score; yours
    // is a detail.
    if (hud.teams) {
        g.font = "800 16px system-ui, sans-serif";
        for (const t of hud.teams) {
            g.fillStyle = TEAM_INK[t.team] || "#cdd8f5";
            g.fillText(`${t.team.toUpperCase()}  ${frags ? t.frags : Math.floor(t.score)}`, x, y);
            y += 20;
        }
        y += 4;
    }

    const rows = hud.scores.slice().sort((a, b) =>
        frags ? b.frags - a.frags || a.deaths - b.deaths : b.score - a.score);
    const shown = hud.showScores ? rows : rows.slice(0, 6);

    g.font = "600 12px system-ui, sans-serif";
    for (const s of shown) {
        const me = s.cid === hud.selfCid;
        // Your own row stays yellow so you can find yourself; a team row uses the team
        // ink; everyone else's name is drawn in their own player colour.
        g.fillStyle = me ? "#ffe24a" : s.team ? (TEAM_INK[s.team] || "#cdd8f5") : (s.c || "#cdd8f5");
        g.fillText(`${s.name}   ${stat(s)}`, x, y);
        y += 16;
    }
    if (frags && hud.showScores) {
        g.fillStyle = "#4a5675";
        g.font = "600 10px system-ui, sans-serif";
        g.fillText("frags / deaths", x, y + 2);
    } else if (rows.length > shown.length) {
        g.fillStyle = "#4a5675";
        g.font = "600 10px system-ui, sans-serif";
        g.fillText(`+${rows.length - shown.length} more — hold Tab`, x, y + 2);
    }
}

// Chat and the kill feed, top-left, newest last. Fades out on its own
// so the screen goes quiet again; the console keeps the full log.
function drawChat(g, canvas, hud) {
    const INK = { chat: "#ffe24a", team: "#4dffa6", frag: "#ff9f6e", system: "#8ea0c8", error: "#ff5b6e" };
    g.textAlign = "left";
    g.textBaseline = "top";
    g.font = "600 12px system-ui, sans-serif";
    let y = 56;
    for (const line of hud.chat || []) {
        g.globalAlpha = Math.min(1, line.ttl / 1.2); // last second or so fades
        g.fillStyle = INK[line.kind] || "#cdd8f5";
        g.fillText(line.text, 14, y);
        y += 16;
    }
    g.globalAlpha = 1;
}

// The match banner: what's ending, or what ended.
function drawMatch(g, canvas, hud) {
    const m = hud.match;
    if (!m || m.state !== "over") return;
    g.textAlign = "center";
    g.textBaseline = "middle";
    const cx = canvas.width / 2, cy = canvas.height / 2 - 60;
    g.fillStyle = "rgba(8,11,18,.72)";
    g.fillRect(0, cy - 46, canvas.width, 92);
    g.font = "800 30px system-ui, sans-serif";
    g.fillStyle = "#ffe24a";
    g.fillText(m.winner ? `${String(m.winner).toUpperCase()} WINS` : "DRAW", cx, cy - 10);
    g.font = "600 13px system-ui, sans-serif";
    g.fillStyle = "#8ea0c8";
    g.fillText(`${m.reason === "fraglimit" ? "frag limit" : "time limit"} · next round in ${m.resetIn}s`, cx, cy + 22);
    g.textBaseline = "top";
}

// The inventory HOTBAR (Set 12.5): everything you're holding, as a row of icon slots with counts,
// top-left and out of the way of the bottom weapon bar. Reads the item->count map (hud.inv) and the
// item manifest (hud.items — an icon key + tint per item name). Position/feel is Eric's to refine.
function drawHotbar(g, hud) {
    if (hud.hotbar === false) return; // a plugin (grid inventory) suppressed the default hotbar
    const inv = hud.inv, items = hud.items || {};
    if (!inv) return;
    const names = Object.keys(inv).filter((n) => inv[n] > 0).sort();
    if (!names.length) return;
    const S = 44, gap = 6, x0 = 14, y = 14;
    for (let i = 0; i < names.length; i++) {
        const n = names[i], count = inv[n], meta = items[n] || {};
        const x = x0 + i * (S + gap);
        g.fillStyle = "rgba(12,16,24,.72)"; g.beginPath(); g.roundRect(x, y, S, S, 8); g.fill();
        g.strokeStyle = "#2a3550"; g.lineWidth = 1; g.beginPath(); g.roundRect(x, y, S, S, 8); g.stroke();
        g.save(); g.translate(x + S / 2, y + S / 2 - 2); g.scale(1.4, 1.4);
        drawItemIcon(g, n, meta.icon, meta.tint); g.restore();
        g.fillStyle = "#e6edff"; g.font = "700 11px system-ui, sans-serif"; g.textAlign = "right"; g.textBaseline = "alphabetic";
        g.fillText(count, x + S - 5, y + S - 5);
    }
    g.textBaseline = "top";
}

function drawHud(g, canvas, hud) {
    g.textBaseline = "top";
    // The wave title only means something where waves happen.
    if (!hud.mode || hud.mode.name === "coop") {
        g.font = "700 20px system-ui, sans-serif";
        g.fillStyle = "#ffe24a";
        g.textAlign = "center";
        const label = hud.phase === "won" ? "YOU SURVIVED — GG" : `WAVE ${hud.wave}`;
        g.fillText(label, canvas.width / 2, 12);
        if (hud.phase === "intermission") {
            g.font = "600 13px system-ui, sans-serif";
            g.fillStyle = "#8ea0c8";
            g.fillText("next wave incoming…", canvas.width / 2, 38);
        }
    }
    drawScores(g, canvas, hud);
    drawChat(g, canvas, hud);
    drawMatch(g, canvas, hud);
    drawHotbar(g, hud);

    // self HP bar, armour over it, falling warning
    if (hud.self) {
        const frac = Math.max(0, Math.min(1, hud.self.hp / hud.self.mx));
        // Lifted clear of the weapon carousel below it, which now carries the selected
        // weapon's name in the gap the health bar used to sit in.
        const bw = 200, x = canvas.width / 2 - bw / 2, y = canvas.height - 100;
        g.fillStyle = "rgba(0,0,0,.5)"; g.fillRect(x, y, bw, 8);
        g.fillStyle = frac > 0.3 ? "#39d1ff" : "#ff5b6e"; g.fillRect(x, y, bw * frac, 8);
        // Armour rides directly above health, because that's the
        // order it's spent in.
        const ar = hud.self.ar || 0;
        if (ar > 0) {
            const af = Math.max(0, Math.min(1, ar / (hud.self.arMax || 100)));
            g.fillStyle = "rgba(0,0,0,.5)"; g.fillRect(x, y - 7, bw, 5);
            g.fillStyle = "#4aa8ff"; g.fillRect(x, y - 7, bw * af, 5);
        }
        // Jetpack fuel gauge, above the armour/health stack. Green when armed
        // (Z-toggled on), amber when carried but idle. Empty = it explodes, so
        // the bar draining is your cue to jettison (hold Z).
        const jet = hud.self.jet;
        if (jet) {
            const jf = Math.max(0, Math.min(1, jet.fuel / (jet.max || 100)));
            const jy = y - (ar > 0 ? 15 : 9);
            g.fillStyle = "rgba(0,0,0,.5)"; g.fillRect(x, jy, bw, 5);
            g.fillStyle = jf < 0.25 ? "#ff5b6e" : jet.on ? "#4dffa6" : "#ffb020";
            g.fillRect(x, jy, bw * jf, 5);
            g.fillStyle = "#8ea0c8"; g.font = "700 9px system-ui, sans-serif"; g.textAlign = "left";
            g.fillText(jet.on ? "JET ⛽ (Z off · hold Z drop)" : "JET ⛽ (Z on)", x, jy - 11);
            g.textAlign = "center";
        }
        if (hud.falling) {
            g.fillStyle = "#ff5b6e"; g.font = "800 16px system-ui, sans-serif"; g.textAlign = "center";
            g.fillText("⚠ FALLING — GET BACK UP", canvas.width / 2, y - 24);
        }
    }

    // The shooter weapon carousel is a PLUGIN now (Set 12.9): the shooter-hotbar plugin's ui.carousel,
    // rendered on the macro-widget DOM overlay (widget-dom.js). The engine no longer draws a weapon strip
    // — a bare game with no hotbar plugin falls back to the generic top-left item list (drawHotbar).

    // version, tucked in the bottom-left where it's legible but out of the way
    g.font = "600 10px system-ui, sans-serif";
    g.textAlign = "left";
    g.fillStyle = "rgba(138,151,216,.4)";
    g.fillText(`v${VERSION}`, 8, canvas.height - 8);
    g.textAlign = "center";

    // crosshair with cooldown ring
    const readyFrac = hud.cdMax > 0 ? 1 - hud.cd / hud.cdMax : 1;
    if (hud.mouse) {
        const mx = hud.mouse.x, my = hud.mouse.y;
        const ready = readyFrac >= 1;
        g.strokeStyle = ready ? "rgba(120,255,180,.85)" : "rgba(255,255,255,.5)";
        g.lineWidth = 1.5;
        g.beginPath();
        g.moveTo(mx - 14, my); g.lineTo(mx - 5, my);
        g.moveTo(mx + 5, my); g.lineTo(mx + 14, my);
        g.moveTo(mx, my - 14); g.lineTo(mx, my - 5);
        g.moveTo(mx, my + 5); g.lineTo(mx, my + 14);
        g.stroke();
        g.beginPath();
        g.strokeStyle = ready ? "rgba(120,255,180,.95)" : "rgba(255,196,74,.9)";
        g.lineWidth = ready ? 2.5 : 3;
        g.arc(mx, my, 11, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * readyFrac);
        g.stroke();
        // Charge wind-up (dodgeball / fish): an outer arc that fills as you hold, brightening
        // to cyan at full charge — so you can see how much launch speed you've banked.
        if (hud.chargeFrac > 0) {
            g.beginPath();
            g.strokeStyle = hud.chargeFrac >= 1 ? "rgba(130,255,255,.95)" : "rgba(90,200,255,.9)";
            g.lineWidth = 3;
            g.arc(mx, my, 17, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.min(1, hud.chargeFrac));
            g.stroke();
        }
    }

    // "denied" flashes at the click point
    for (const d of hud.denies || []) {
        const t = d.ttl / d.max;
        const grow = 1 - t;
        g.globalAlpha = t;
        g.strokeStyle = "#ff5b6e";
        g.lineWidth = 2 + grow * 9;
        g.beginPath();
        g.arc(d.x, d.y, 12 + grow * 26, 0, Math.PI * 2);
        g.stroke();
        g.globalAlpha = 1;
    }

    // the plugin's macro-widgets (Set 12.5) are a DOM overlay now (widget-dom.js), not drawn here
    // the trowel's block wheel, on top of everything when it's up
    if (hud.wheel && hud.wheel.open) drawBlockWheel(g, canvas, hud.wheel);
    // the ONE composable radial picker (Set 21.5) — a plugin's ui.wheel, drawn from its options
    if (hud.picker) drawWheel(g, canvas, hud.picker);
    // a plugin's template menu (UI framework D), same radial layout
    if (hud.menu) drawMenu(g, canvas, hud.menu);
}

// The block wheel (C, trowel in hand): a ring of wedges, one per block type you're
// carrying, coloured like the block itself. Wedge 0 is at the top and they run clockwise —
// the same layout wheel.js hit-tests the pointer against, so the highlight lands where the
// click will. The hovered block's name sits in the hub.
function drawBlockWheel(g, canvas, wheel) {
    const items = wheel.items || [];
    const cx = canvas.width / 2, cy = canvas.height / 2;
    const rOut = 150, rIn = 54;
    g.fillStyle = "rgba(8,11,18,.5)";
    g.beginPath(); g.arc(cx, cy, rOut + 14, 0, Math.PI * 2); g.fill();
    g.textAlign = "center"; g.textBaseline = "middle";
    if (!items.length) {
        g.fillStyle = "#8ea0c8"; g.font = "700 14px system-ui, sans-serif";
        g.fillText("no blocks — mine some with the mallet", cx, cy);
        g.textBaseline = "alphabetic";
        return;
    }
    const n = items.length, wedge = (Math.PI * 2) / n;
    for (let i = 0; i < n; i++) {
        const it = items[i];
        const th = i * wedge - Math.PI / 2;          // wedge centre, canvas angle
        const on = it.name === wheel.sel;
        g.beginPath();
        g.arc(cx, cy, rOut, th - wedge / 2, th + wedge / 2);
        g.arc(cx, cy, rIn, th + wedge / 2, th - wedge / 2, true);
        g.closePath();
        g.globalAlpha = on ? 1 : 0.8;
        g.fillStyle = it.fill; g.fill();
        g.strokeStyle = on ? "#fff" : "rgba(0,0,0,.5)"; g.lineWidth = on ? 3 : 1.5; g.stroke();
        g.globalAlpha = 1;
        const lr = (rIn + rOut) / 2;
        const lx = cx + Math.cos(th) * lr, ly = cy + Math.sin(th) * lr;
        // A custom block (Set 19.2) shows its 6×6 pixel design in the wedge; a plain material just its name.
        if (it.custom) { g.save(); g.translate(lx, ly - 8); drawCustomBlockIcon(g, it.def, 30); g.restore(); }
        g.fillStyle = "#fff";
        g.font = "700 12px system-ui, sans-serif"; g.fillText(it.custom ? (it.label || "block") : it.name, lx, ly + (it.custom ? 14 : -7));
        if (!it.custom) { g.font = "700 11px system-ui, sans-serif"; g.fillText(String(it.count), lx, ly + 8); }
    }
    g.fillStyle = "#ffe24a"; g.font = "800 15px system-ui, sans-serif";
    g.fillText(wheel.sel || "pick a block", cx, cy);
    g.textBaseline = "alphabetic";
}

// The ONE radial picker draw (Set 21.5): options come from a plugin's ui.wheel. Each wedge draws whatever
// its option carries — an item icon (icon+tint, resolved from the manifest in the snapshot), or a solid
// colour swatch (a block material) — with an optional count. `sel` is the hovered index. drawBlockWheel /
// the old drawItemWheel collapsed into this; the geometry still matches (rOut/rIn, wedge = 2π/n, top-start).
function drawWheel(g, canvas, picker) {
    const options = picker.options || [];
    const cx = canvas.width / 2, cy = canvas.height / 2, rOut = 150, rIn = 54;
    g.fillStyle = "rgba(8,11,18,.55)"; g.beginPath(); g.arc(cx, cy, rOut + 14, 0, Math.PI * 2); g.fill();
    g.textAlign = "center"; g.textBaseline = "middle";
    if (!options.length) {
        g.fillStyle = "#8ea0c8"; g.font = "700 14px system-ui, sans-serif";
        g.fillText("nothing to pick", cx, cy); g.textBaseline = "alphabetic"; return;
    }
    const n = options.length, wedge = (Math.PI * 2) / n;
    for (let i = 0; i < n; i++) {
        const o = options[i], th = i * wedge - Math.PI / 2, on = i === picker.sel;
        g.beginPath();
        g.arc(cx, cy, rOut, th - wedge / 2, th + wedge / 2);
        g.arc(cx, cy, rIn, th + wedge / 2, th - wedge / 2, true);
        g.closePath();
        g.fillStyle = on ? "#22314b" : "#131c2b"; g.fill();
        g.strokeStyle = on ? "#ffe24a" : "rgba(255,255,255,.12)"; g.lineWidth = on ? 3 : 1.5; g.stroke();
        const lr = (rIn + rOut) / 2, lx = cx + Math.cos(th) * lr, ly = cy + Math.sin(th) * lr;
        if (o.color) { // a material swatch (the block wheel): a filled disc in the wedge
            g.beginPath(); g.arc(lx, ly - 2, 15, 0, Math.PI * 2); g.fillStyle = o.color; g.fill();
            g.strokeStyle = "rgba(0,0,0,.35)"; g.lineWidth = 1.5; g.stroke();
        } else {
            g.save(); g.translate(lx, ly - 4); g.scale(1.6, 1.6); drawItemIcon(g, o.value, o.icon, o.tint); g.restore();
        }
        if (o.count != null) { g.fillStyle = "#e6edff"; g.font = "700 11px system-ui, sans-serif"; g.fillText(String(o.count), lx, ly + 16); }
    }
    const selLabel = picker.sel >= 0 && options[picker.sel] ? (options[picker.sel].label || options[picker.sel].value) : "pick one";
    g.fillStyle = "#ffe24a"; g.font = "800 14px system-ui, sans-serif";
    g.fillText(selLabel, cx, cy);
    g.textBaseline = "alphabetic";
}

// A plugin's template menu (UI framework D): the same radial layout as the block wheel, but the
// wedges are the plugin's options (label + an optional icon), and the hub shows the hovered one or
// the menu title. Uses the same wheel.js hit-test (menuHover), so the highlight lands where you click.
const MENU_ICON = { fire: "🔥", seed: "🌱", sprout: "🌱", crop: "🌿", food: "🍖", meal: "🍗" };
function drawMenu(g, canvas, menu) {
    const opts = menu.options || [];
    const cx = canvas.width / 2, cy = canvas.height / 2;
    const rOut = 150, rIn = 54;
    g.fillStyle = "rgba(8,11,18,.55)";
    g.beginPath(); g.arc(cx, cy, rOut + 14, 0, Math.PI * 2); g.fill();
    g.textAlign = "center"; g.textBaseline = "middle";
    if (!opts.length) {
        g.fillStyle = "#8ea0c8"; g.font = "700 14px system-ui, sans-serif";
        g.fillText(menu.title || "no options", cx, cy); g.textBaseline = "alphabetic"; return;
    }
    const n = opts.length, wedge = (Math.PI * 2) / n;
    for (let i = 0; i < n; i++) {
        const o = opts[i], th = i * wedge - Math.PI / 2, on = i === menu.sel;
        g.beginPath();
        g.arc(cx, cy, rOut, th - wedge / 2, th + wedge / 2);
        g.arc(cx, cy, rIn, th + wedge / 2, th - wedge / 2, true);
        g.closePath();
        g.fillStyle = on ? "rgba(217,85,15,.92)" : "rgba(24,28,40,.92)";
        g.fill();
        g.strokeStyle = on ? "#fff" : "rgba(0,0,0,.5)"; g.lineWidth = on ? 3 : 1.5; g.stroke();
        const lr = (rIn + rOut) / 2, lx = cx + Math.cos(th) * lr, ly = cy + Math.sin(th) * lr;
        const ic = MENU_ICON[o.icon];
        g.fillStyle = "#fff";
        if (ic) { g.font = "18px system-ui, sans-serif"; g.fillText(ic, lx, ly - 9); }
        g.font = "700 12px system-ui, sans-serif"; g.fillText(o.label || o.id, lx, ly + (ic ? 11 : 0));
    }
    const sel = menu.sel >= 0 && opts[menu.sel] ? (opts[menu.sel].label || opts[menu.sel].id) : (menu.title || "choose");
    g.fillStyle = "#ffe24a"; g.font = "800 15px system-ui, sans-serif"; g.fillText(sel, cx, cy);
    g.textBaseline = "alphabetic";
}
